// Orbit projects — featured project carousel with depth-based orbit animation
import { gsap, ScrollTrigger } from '../gsap-config.js';

let CustomEase;
let customEaseReady = false;

async function ensureCustomEase() {
	if (customEaseReady) return CustomEase;

	try {
		const mod = await import('gsap/CustomEase');
		CustomEase = mod.CustomEase || mod.default;

		if (CustomEase?.create) {
			gsap.registerPlugin(CustomEase);
			CustomEase.create('osmo-ease', '0.625, 0.05, 0, 1');
		}
	} catch {
		CustomEase = null;
	}

	customEaseReady = true;
	return CustomEase;
}

function cleanupContainer(container) {
	if (container._orbitScrollTrigger) {
		container._orbitScrollTrigger.kill();
		container._orbitScrollTrigger = null;
	}

	if (container._orbitStepTimeline) {
		container._orbitStepTimeline.kill();
		container._orbitStepTimeline = null;
	}

	if (container._orbitDelayedCall) {
		container._orbitDelayedCall.kill();
		container._orbitDelayedCall = null;
	}

	container._orbitRotations?.forEach((rotation) => rotation.kill());
	container._orbitRotations = null;

	container._orbitTileStates?.forEach((state) => gsap.killTweensOf(state));
	container._orbitTileStates = null;

	container._orbitResizeObserver?.disconnect();
	container._orbitResizeObserver = null;
}

function setTileInteractivity(tile, isActive) {
	tile.setAttribute('data-orbit-tiles-item-status', isActive ? 'active' : 'not-active');

	const link = tile.querySelector('.orbit-tiles__link');
	if (link) {
		if (isActive) {
			link.removeAttribute('tabindex');
			link.removeAttribute('aria-hidden');
		} else {
			link.setAttribute('tabindex', '-1');
			link.setAttribute('aria-hidden', 'true');
		}
	}

	const industryLink = tile.querySelector('.orbit-tiles__industry');
	if (industryLink) {
		if (isActive) {
			industryLink.removeAttribute('tabindex');
			industryLink.removeAttribute('aria-hidden');
			industryLink.classList.add('not-hide-cursor');
		} else {
			industryLink.setAttribute('tabindex', '-1');
			industryLink.setAttribute('aria-hidden', 'true');
			industryLink.classList.remove('not-hide-cursor');
		}
	}
}

export function initOrbitProjects() {
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	const stepEaseRef = { value: 'power2.inOut' };

	ensureCustomEase().then((easePlugin) => {
		if (easePlugin) stepEaseRef.value = 'osmo-ease';
	});

	document.querySelectorAll('[data-orbit-tiles-init]').forEach((container) => {
		cleanupContainer(container);

		const list = container.querySelector('[data-orbit-tiles-list]');
		const tiles = Array.from(container.querySelectorAll('[data-orbit-tiles-item]'));
		const tileCount = tiles.length;

		if (tileCount < 2) return;

		const radiusXMultiplier = 1;
		const radiusYMultiplier = 0;
		const blurMultiplier = 0.04;
		const minScale = 0.2;
		const minOpacity = 1;
		const minDarkness = 0.3;

		const moveDuration = 2.5;
		const pauseDuration = 0;
		const staggerAmount = moveDuration * 0.03;
		const linearRotateDuration = prefersReducedMotion ? 0 : 24;

		const tileStates = Array.from(tiles, () => ({ progress: 0 }));
		container._orbitTileStates = tileStates;

		let isActive = false;
		let stepTimeline;
		let delayedCall;
		let activeTileIndex = -1;

		const getActiveIndex = () =>
			tileStates.reduce((closest, state, index) => {
				const currentOffset = ((index - state.progress) % tileCount + tileCount) % tileCount;
				const current = Math.min(currentOffset, tileCount - currentOffset);
				const closestOffset = ((closest - tileStates[closest].progress) % tileCount + tileCount) % tileCount;
				const previous = Math.min(closestOffset, tileCount - closestOffset);
				return current < previous ? index : closest;
			}, 0);

		const updateTileStatus = () => {
			const currentActiveIndex = getActiveIndex();
			if (currentActiveIndex === activeTileIndex) return;

			activeTileIndex = currentActiveIndex;

			tiles.forEach((tile, index) => {
				setTileInteractivity(tile, index === activeTileIndex);
			});
		};

		const renderOrbit = () => {
			const tileWidth = tiles[0].offsetWidth;
			const radiusX = tileWidth * radiusXMultiplier;
			const radiusY = tileWidth * radiusYMultiplier;
			const maxBlur = tileWidth * blurMultiplier;

			updateTileStatus();

			tiles.forEach((tile, index) => {
				const angle = ((index - tileStates[index].progress) / tileCount) * Math.PI * 2;
				const depth = (Math.cos(angle) + 1) / 2;
				const adjustedDepth = Math.pow(depth, 1.3);

				const scale = gsap.utils.interpolate(minScale, 1, adjustedDepth);
				const opacity = gsap.utils.interpolate(minOpacity, 1, adjustedDepth);
				const blur = gsap.utils.interpolate(maxBlur, 0, adjustedDepth);
				const brightness = gsap.utils.interpolate(minDarkness, 1, adjustedDepth);

				gsap.set(tile, {
					x: Math.sin(angle) * radiusX,
					y: Math.cos(angle) * radiusY,
					scale,
					opacity,
					filter: `blur(${blur}px) brightness(${brightness})`,
					zIndex: Math.round(adjustedDepth * 1000),
				});
			});
		};

		const rotations =
			!list || linearRotateDuration === 0
				? []
				: [
						gsap.to(list, {
							rotate: 360,
							duration: linearRotateDuration,
							ease: 'none',
							repeat: -1,
							paused: true,
						}),
						gsap.to(tiles, {
							rotate: -360,
							duration: linearRotateDuration,
							ease: 'none',
							repeat: -1,
							paused: true,
						}),
					];

		container._orbitRotations = rotations;

		const goToNextTile = () => {
			if (!isActive || prefersReducedMotion) return;

			const activeIndex = getActiveIndex();
			const orderedStates = tileStates
				.map((state, index) => ({
					state,
					offset: (index - activeIndex + tileCount) % tileCount,
				}))
				.sort((a, b) => a.offset - b.offset);

			stepTimeline = gsap.timeline({
				paused: true,
				onComplete: () => {
					if (isActive) delayedCall = gsap.delayedCall(pauseDuration, goToNextTile);
				},
			});

			container._orbitStepTimeline = stepTimeline;

			orderedStates.forEach(({ state }, index) => {
				stepTimeline.to(
					state,
					{
						progress: state.progress + 1,
						duration: moveDuration,
						ease: stepEaseRef.value,
						onUpdate: renderOrbit,
					},
					index * staggerAmount
				);
			});

			stepTimeline.play();
		};

		const pauseOrbit = () => {
			isActive = false;
			stepTimeline?.pause();
			delayedCall?.pause();
			rotations.forEach((rotation) => rotation.pause());
		};

		const playOrbit = () => {
			if (prefersReducedMotion) {
				renderOrbit();
				return;
			}

			isActive = true;
			rotations.forEach((rotation) => rotation.play());

			if (stepTimeline && stepTimeline.progress() < 1) {
				stepTimeline.play();
			} else {
				goToNextTile();
			}
		};

		renderOrbit();

		const section = container.closest('.orbit-projects-section');
		if (section && !section.dataset.orbitHeightLocked) {
			section.style.minHeight = `${section.offsetHeight}px`;
			section.dataset.orbitHeightLocked = 'true';
		}

		const resizeObserver = new ResizeObserver(renderOrbit);
		resizeObserver.observe(container);
		container._orbitResizeObserver = resizeObserver;

		container._orbitScrollTrigger = ScrollTrigger.create({
			trigger: container,
			start: 'top bottom',
			end: 'bottom top',
			onToggle: (self) => (self.isActive ? playOrbit() : pauseOrbit()),
		});
	});
}

function bootstrap() {
	requestAnimationFrame(() => {
		if (document.querySelector('[data-orbit-tiles-init]')) {
			initOrbitProjects();
		}
	});
}

bootstrap();
